import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, ExternalLink, FileArchive, FileText, RotateCcw, Upload } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { FicheIndicateur } from '../components/qualiopi/FicheIndicateur';
import { ModaleDossierAudit } from '../components/qualiopi/ModaleDossierAudit';
import {
  ModaleApplicabilite,
  ModaleDepotPreuve,
  ModaleRattachements,
  ModaleRetraitPreuve,
} from '../components/qualiopi/ModalesQualiopi';
import { instantLisible, jourLisible } from '../components/veille/format';
import { usePerimetre } from '../site/usePerimetre';
import { useSites } from '../lib/useSites';
import { ErreurApi } from '../lib/api';
import {
  useQualiopi,
  type IndicateurQualiopi,
  type PreuveQualiopi,
  type Rattachement,
  type ReponseQualiopi,
  type StatutIndicateur,
} from '../lib/useQualiopi';

type Vue = StatutIndicateur | 'TOUS';

const VUES: readonly Vue[] = ['TOUS', 'A_COMPLETER', 'CONFORME', 'NON_APPLICABLE'];

const LIBELLES_VUE: Record<Vue, string> = {
  TOUS: 'Tous les indicateurs',
  A_COMPLETER: 'À compléter',
  CONFORME: 'Conformes',
  NON_APPLICABLE: 'Non applicables',
};

const PASTILLES_NUMERO: Record<StatutIndicateur, string> = {
  CONFORME: 'bg-codialis-mint/20 text-codialis-navy',
  A_COMPLETER: 'bg-amber-100 text-amber-800',
  NON_APPLICABLE: 'bg-gray-100 text-codialis-gray',
};

type Geste =
  | { type: 'DEPOT'; rattachement: Rattachement | null }
  | { type: 'RATTACHEMENTS'; preuve: PreuveQualiopi }
  | { type: 'RETRAIT'; preuve: PreuveQualiopi }
  | { type: 'APPLICABILITE'; indicateur: IndicateurQualiopi }
  | { type: 'DOSSIER_AUDIT' };

/** Recherche sans casse ni accent : « referent » trouve « référent ». */
function cleRecherche(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function messageErreur(erreur: unknown): string {
  return erreur instanceof ErreurApi ? erreur.message : 'L’espace de conformité n’a pas pu être chargé.';
}

function resumeIndicateur(indicateur: IndicateurQualiopi): string {
  if (indicateur.nonApplicable) return `Non applicable : ${indicateur.nonApplicable.motif}`;
  if (indicateur.statut === 'CONFORME') return 'Toutes les exigences sont remplies';
  const [premier, ...autres] = indicateur.manques;
  return autres.length > 0 ? `${premier} · et ${autres.length} autre${autres.length > 1 ? 's' : ''}` : (premier ?? '');
}

function CarteCertification({ donnees }: { donnees: ReponseQualiopi }): JSX.Element {
  const { certification } = donnees;
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="qualiopi-badge rounded px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
              Qualiopi
            </span>
            <Pastille texte={certification.libelle} ton={certification.ton} />
          </div>
          <h2 className="font-display mt-2 text-lg font-bold text-codialis-navy">Certification relevée par la veille</h2>
          <p className="text-sm text-codialis-gray">
            {certification.echeance
              ? `${certification.echeance.nature} : ${jourLisible(certification.echeance.date)} · ${certification.echeance.intitule} · ${certification.echeance.libelle}`
              : 'Aucune échéance de certification saisie dans la veille réglementaire.'}
          </p>
        </div>
        <Link
          to="/ped-veille"
          className="inline-flex min-h-[44px] shrink-0 items-center text-sm font-semibold text-codialis-sky hover:underline"
        >
          Veille réglementaire
          {certification.evolutionsAAnalyser > 0 &&
            ` · ${certification.evolutionsAAnalyser} évolution${certification.evolutionsAAnalyser > 1 ? 's' : ''} à analyser`}
        </Link>
      </div>
      <ul className="mt-3 space-y-2">
        {certification.organismes.map((organisme) => (
          <li
            key={organisme.numeroDeclaration ?? organisme.antennes.join('-')}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 p-3"
          >
            {organisme.antennes.map((antenne) => (
              <PastilleSite key={antenne} site={antenne} />
            ))}
            <span className="order-last w-full text-sm text-codialis-navy sm:order-none sm:w-auto sm:min-w-0 sm:flex-1">
              {organisme.numeroDeclaration && (
                <span className="font-mono text-xs text-codialis-gray">NDA {organisme.numeroDeclaration} · </span>
              )}
              {organisme.detail}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-codialis-gray">
        Indicateurs du{' '}
        <a
          href={donnees.version.lien}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-codialis-sky hover:underline"
        >
          {donnees.version.texte}
          <ExternalLink className="h-3 w-3" aria-hidden />
          <span className="sr-only">(nouvel onglet)</span>
        </a>
        , en vigueur le {jourLisible(donnees.version.enVigueurLe)}.
      </p>
    </div>
  );
}

/**
 * Espace de conformité Qualiopi (phase 5, TF-431) : les 33 indicateurs du
 * référentiel national qualité, leur statut — conforme, à compléter, non
 * applicable —, ce que la plateforme en contrôle et les preuves rattachées,
 * avec l'accès à leurs documents sources.
 *
 * L'écran ne décide rien : statuts, manques et refus viennent de
 * `/api/qualiopi`. La fiche d'un indicateur s'ouvre par `?indicateur=n`.
 * « Dossier d'audit » exporte le rapport et ses pièces pour l'auditeur
 * (TF-432, `ModaleDossierAudit`).
 */
export function QualiopiPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useQualiopi(site);
  const { data: sitesUtilisateur } = useSites();
  const [parametres, setParametres] = useSearchParams();
  const [vue, setVue] = useState<Vue>('TOUS');
  const [critere, setCritere] = useState('');
  const [recherche, setRecherche] = useState('');
  const [geste, setGeste] = useState<Geste | null>(null);

  const numero = Number(parametres.get('indicateur')) || null;
  const indicateurs = useMemo(() => data?.criteres.flatMap((ligne) => ligne.indicateurs) ?? [], [data]);
  const ouvert = indicateurs.find((indicateur) => indicateur.numero === numero) ?? null;
  const preuvesParId = useMemo(() => new Map((data?.preuves ?? []).map((preuve) => [preuve.id, preuve])), [data]);

  const ouvrir = (suivant: number | null): void => {
    setParametres(
      (avant) => {
        const prochains = new URLSearchParams(avant);
        if (suivant === null) prochains.delete('indicateur');
        else prochains.set('indicateur', String(suivant));
        return prochains;
      },
      { replace: true },
    );
  };

  const terme = cleRecherche(recherche);
  const retenu = (indicateur: IndicateurQualiopi): boolean => {
    if (vue !== 'TOUS' && indicateur.statut !== vue) return false;
    if (critere && String(indicateur.critere) !== critere) return false;
    if (!terme) return true;
    return [String(indicateur.numero), indicateur.titre, indicateur.enonce, ...indicateur.manques].some((champ) =>
      cleRecherche(champ).includes(terme),
    );
  };
  const filtreActif = vue !== 'TOUS' || critere !== '' || recherche.trim() !== '';
  const compte = (cle: Vue): number =>
    cle === 'TOUS' ? indicateurs.length : indicateurs.filter((indicateur) => indicateur.statut === cle).length;

  const sitesDepot = (sitesUtilisateur?.sites ?? []).map((ligne) => ({ code: ligne.code, nom: ligne.code }));
  const synthese = data?.synthese;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Conformité Qualiopi"
        sousTitre={`${synthese ? `Les ${synthese.indicateurs}` : 'Les'} indicateurs du référentiel national qualité, ce que la plateforme en contrôle et les preuves rattachées`}
        actions={
          <>
            <BarreSites />
            {data && (
              <BoutonSecondaire onClick={() => setGeste({ type: 'DOSSIER_AUDIT' })}>
                <FileArchive className="mr-2 h-4 w-4" aria-hidden />
                Dossier d’audit
              </BoutonSecondaire>
            )}
            {data?.peutDeposer && (
              <BoutonPrincipal onClick={() => setGeste({ type: 'DEPOT', rattachement: null })}>
                <Upload className="mr-2 h-4 w-4" aria-hidden />
                Déposer une preuve
              </BoutonPrincipal>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Conformes"
          valeur={synthese ? `${synthese.conformes} / ${synthese.applicables}` : '—'}
          ton="mint"
          detail="Indicateurs applicables dont toutes les exigences sont remplies"
        />
        <Compteur
          libelle="À compléter"
          valeur={synthese?.aCompleter ?? '—'}
          ton={synthese && synthese.aCompleter > 0 ? 'warm' : 'navy'}
          detail="Un contrôle ou une preuve attendue manque"
        />
        <Compteur
          libelle="Non applicables"
          valeur={synthese?.nonApplicables ?? '—'}
          detail="Déclarés par la direction, motif à l’appui"
        />
        <Compteur
          libelle="Preuves valables"
          valeur={synthese?.preuvesValables ?? '—'}
          ton="blue"
          detail="Déposées, ni retirées ni expirées"
        />
      </div>

      {isLoading && <Chargement libelle="Lecture des indicateurs…" />}
      {error && <MessageErreur message={messageErreur(error)} />}

      {data && (
        <>
          <CarteCertification donnees={data} />

          <Onglets
            etiquette="Indicateurs"
            onglets={VUES.map((cle) => ({ cle, libelle: LIBELLES_VUE[cle], compte: compte(cle) }))}
            actif={vue}
            onChanger={setVue}
            idPanneau="panneau-qualiopi"
            actions={
              <>
                <ChampRecherche
                  valeur={recherche}
                  onChange={setRecherche}
                  etiquette="Rechercher un indicateur"
                  placeholder="Numéro, intitulé, manque…"
                />
                <ChampFiltre valeur={critere} onChange={setCritere} etiquette="Critère">
                  <option value="">Tous les critères</option>
                  {data.criteres.map((ligne) => (
                    <option key={ligne.numero} value={ligne.numero}>
                      Critère {ligne.numero} · {ligne.titre}
                    </option>
                  ))}
                </ChampFiltre>
                {filtreActif && (
                  <button
                    type="button"
                    onClick={() => {
                      setVue('TOUS');
                      setCritere('');
                      setRecherche('');
                    }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    Réinitialiser
                  </button>
                )}
              </>
            }
          />

          <PanneauOnglet id="panneau-qualiopi" actif={vue} className="space-y-6">
            {data.criteres.map((ligne) => {
              const visibles = ligne.indicateurs.filter(retenu);
              if (visibles.length === 0) return null;
              const parCritere = data.synthese.parCritere.find((candidat) => candidat.critere === ligne.numero);
              return (
                <section key={ligne.numero} className="overflow-hidden rounded-2xl bg-white shadow-card">
                  <header className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:px-6 sm:py-4">
                    <div className="min-w-0">
                      <h2 className="font-display font-bold text-codialis-navy">
                        Critère {ligne.numero} · {ligne.titre}
                      </h2>
                      <p className="text-xs text-codialis-gray">{ligne.enonce}</p>
                    </div>
                    {parCritere && (
                      <span className="shrink-0 text-xs font-semibold text-codialis-gray">
                        {parCritere.conformes} / {parCritere.applicables} conforme{parCritere.conformes > 1 ? 's' : ''}
                      </span>
                    )}
                  </header>
                  <ul className="divide-y divide-gray-100">
                    {visibles.map((indicateur) => (
                      <li key={indicateur.numero}>
                        <button
                          type="button"
                          onClick={() => ouvrir(indicateur.numero)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50 sm:px-6"
                        >
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-bold ${PASTILLES_NUMERO[indicateur.statut]}`}
                          >
                            {indicateur.numero}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-codialis-navy">{indicateur.titre}</span>
                            <span className="block truncate text-xs text-codialis-gray">{resumeIndicateur(indicateur)}</span>
                          </span>
                          <span className="hidden shrink-0 items-center gap-1 text-xs text-codialis-gray sm:inline-flex">
                            <FileText className="h-3.5 w-3.5" aria-hidden />
                            {indicateur.preuves.filter((id) => preuvesParId.get(id)?.etat === 'VALABLE').length}
                            <span className="sr-only"> preuves valables</span>
                          </span>
                          <span className="shrink-0">
                            <Pastille texte={indicateur.statutLibelle} ton={indicateur.ton} />
                          </span>
                          <ChevronRight className="hidden h-4 w-4 shrink-0 text-codialis-gray sm:block" aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}

            {indicateurs.filter(retenu).length === 0 && (
              <div className="rounded-2xl bg-white p-6 text-sm text-codialis-gray shadow-card">
                Aucun indicateur ne correspond à ces filtres.
              </div>
            )}
          </PanneauOnglet>

          <p className="text-xs text-codialis-gray">
            Calculé le {instantLisible(data.calculeLe)} sur{' '}
            {data.sites.length > 1 ? `les antennes ${data.sites.map((ligne) => ligne.code).join(', ')}` : `l’antenne ${data.sites[0]?.code ?? ''}`}
            . Les comptes d’activité portent sur les douze derniers mois ; la plateforme dit ce qu’elle tient et ce qui
            manque, elle ne prononce pas la certification.
          </p>

          {ouvert && (
            <Modale
              ouverte={geste === null}
              onFermer={() => ouvrir(null)}
              titre={`Indicateur ${ouvert.numero} · ${ouvert.titre}`}
              sousTitre={`Critère ${ouvert.critere} · ${data.criteres.find((ligne) => ligne.numero === ouvert.critere)?.titre ?? ''}`}
              largeur="max-w-4xl"
            >
              <FicheIndicateur
                indicateur={ouvert}
                preuves={ouvert.preuves.flatMap((id) => {
                  const preuve = preuvesParId.get(id);
                  return preuve ? [preuve] : [];
                })}
                peutDeposer={data.peutDeposer}
                peutDeclarer={data.peutDeclarer}
                onDeposer={(attendu) =>
                  setGeste({ type: 'DEPOT', rattachement: { indicateur: ouvert.numero, attendu } })
                }
                onRattacher={(preuve) => setGeste({ type: 'RATTACHEMENTS', preuve })}
                onRetirer={(preuve) => setGeste({ type: 'RETRAIT', preuve })}
                onApplicabilite={() => setGeste({ type: 'APPLICABILITE', indicateur: ouvert })}
                onFermer={() => ouvrir(null)}
              />
            </Modale>
          )}

          {/* Une fenêtre de geste prend la place de la fiche : deux fenêtres superposées se fermeraient ensemble à Échap. */}
          <ModaleDepotPreuve
            ouverte={geste?.type === 'DEPOT'}
            onFermer={() => setGeste(null)}
            referentiel={data.referentiel}
            formats={data.formats}
            sites={sitesDepot}
            rattachementInitial={geste?.type === 'DEPOT' ? geste.rattachement : null}
          />
          {geste?.type === 'RATTACHEMENTS' && (
            <ModaleRattachements preuve={geste.preuve} referentiel={data.referentiel} onFermer={() => setGeste(null)} />
          )}
          {geste?.type === 'RETRAIT' && <ModaleRetraitPreuve preuve={geste.preuve} onFermer={() => setGeste(null)} />}
          {geste?.type === 'APPLICABILITE' && (
            <ModaleApplicabilite indicateur={geste.indicateur} onFermer={() => setGeste(null)} />
          )}
          {geste?.type === 'DOSSIER_AUDIT' && (
            <ModaleDossierAudit
              referentiel={data.dossierAudit}
              criteres={data.criteres}
              site={site}
              critereInitial={critere ? Number(critere) : null}
              onFermer={() => setGeste(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
